/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';

/** Supported booking types for the explore destinations map filters. */
export type BookingType =
    | 'activityPass'
    | 'camping'
    | 'dayUse'
    | 'nonReservable'
    | 'permits'
    | 'permitsAndPasses'
    | 'poi'
    | 'recArea'
    | 'tickets'
    | 'treePermits'
    | 'venuesAndDayUse'
    | 'venues';

/** Filters state shared by the explore destinations map and its children. */
export interface FiltersState {
    location?: string | null;
    locationId?: string | null;
    lat?: number | null;
    lng?: number | null;
    latSw?: number | null;
    lngSw?: number | null;
    latNe?: number | null;
    lngNe?: number | null;
    bookingType?: BookingType | null;
    hoveredCardId?: string | null;
}

/** Updater: partial state (merged with previous) or a function that receives previous state and returns the next. */
export type SetFiltersState = (
    value: Partial<FiltersState> | ((prev: FiltersState) => FiltersState)
) => void;

const defaultFiltersState: FiltersState = {
    location: null,
    locationId: null,
    lat: null,
    lng: null,
    latSw: null,
    lngSw: null,
    latNe: null,
    lngNe: null,
    bookingType: null,
};

type FiltersContextValue = {
    filters: FiltersState;
    setFilters: SetFiltersState;
};

const FiltersContext = createContext<FiltersContextValue | null>(null);

export interface FiltersProviderProps {
    children: ReactNode;
    /** Optional initial filters; defaults to defaultFiltersState. */
    initialFilters?: Partial<FiltersState>;
}

/**
 * Provider for explore destinations filters state. Wrap map and child components
 * that need to read or update filters.
 */
export function FiltersProvider({ children, initialFilters }: FiltersProviderProps) {
    const [filters, setFilters] = useState<FiltersState>(() => ({
        ...defaultFiltersState,
        ...initialFilters,
    }));

    const setFiltersStable = useCallback<SetFiltersState>((value) => {
        setFilters((prev) =>
            typeof value === 'function' ? value(prev) : { ...prev, ...value }
        );
    }, []);

    const value = useMemo(
        () => ({ filters, setFilters: setFiltersStable }),
        [filters, setFiltersStable]
    );

    return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

/**
 * Hook to read and update explore destinations filters state. Use like useState:
 *   const [filters, setFilters] = useFiltersState();
 * setFilters accepts a partial state object (merged with previous) or an updater function (prev) => nextState.
 */
export function useFiltersState(): [FiltersState, SetFiltersState] {
    const ctx = useContext(FiltersContext);
    if (ctx === null) {
        throw new Error('useFiltersState must be used within an FiltersProvider');
    }
    return [ctx.filters, ctx.setFilters];
}
